/**
 * A phone number field with an international country-code selector.
 *
 * Uses `react-phone-number-input`, which shows a country dropdown (with flag)
 * and automatically prefixes the correct country calling code (e.g. +45 for
 * Denmark). The value stored in Final Form is a plain E.164 string such as
 * "+4524666612", so this is a drop-in replacement for the old plain-text field
 * and needs no changes to how phone numbers are saved.
 */
import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import PhoneInput from 'react-phone-number-input';
// Bundle the flag SVGs into the app so no external CDN request is made.
import flags from 'react-phone-number-input/flags';

import { ValidationError } from '../../components';

// NOTE: we deliberately do NOT `import 'react-phone-number-input/style.css'`.
// Sharetribe's server bundle externalizes node_modules, so that import becomes
// a runtime require() of the raw .css file in Node, which crashes SSR
// ("Unexpected token ':'"). Instead the library's base styles are inlined in
// FieldPhoneNumberInput.module.css (which lives in src/ and is handled safely).
import css from './FieldPhoneNumberInput.module.css';

// Denmark is the default, since most sellers/buyers start there. International
// users just open the dropdown and pick their own country.
const DEFAULT_COUNTRY = 'DK';

const PhoneInputFieldComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    labelClassName,
    input,
    meta,
    // These were passed by the old text-input based field; they don't apply to
    // PhoneInput, so pull them out to avoid forwarding invalid DOM props.
    type,
    format,
    parse,
    ...rest
  } = props;

  const { valid, invalid, touched, error } = meta;
  const hasError = !!(touched && invalid && error);

  const { value, onChange, onBlur, onFocus, name } = input;

  const inputClasses = classNames(css.phoneInput, {
    [css.phoneInputError]: hasError,
  });

  const labelClassMaybe = labelClassName ? { className: labelClassName } : {};
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={id} {...labelClassMaybe}>
          {label}
        </label>
      ) : null}
      <PhoneInput
        {...rest}
        id={id}
        name={name}
        className={inputClasses}
        international
        defaultCountry={DEFAULT_COUNTRY}
        countryCallingCodeEditable={false}
        flags={flags}
        value={value || undefined}
        onChange={val => onChange(val || '')}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      <ValidationError fieldMeta={{ touched: hasError, error }} />
    </div>
  );
};

/**
 * Create a Final Form field with an international phone-number input.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name Name of the field in Final Form
 * @param {string} props.id
 * @param {string?} props.label
 * @returns {JSX.Element}
 */
const FieldPhoneNumberInput = props => {
  return <Field component={PhoneInputFieldComponent} {...props} />;
};

export default FieldPhoneNumberInput;
